import {
  JSX,
  Suspense,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  splitProps,
  useContext,
} from 'solid-js';
import { Ref, mergeRefs } from '@solid-primitives/refs';
import { createEventListener } from '@solid-primitives/event-listener';

import { Toggle } from './Toggle';
import createClickOutside from '../hooks/createClickOutside';
import ScrollView, { useScrollElement } from './Scrollview';
import { cx } from '../utils';
import { ClosableProvider } from './Closable';
import { SignalLike } from './contexts';
import Transition from './transition/Components';
import { Portal } from './transition/solidHack';

// const aneOptions = { ...animateOptions, duration: 0.2 };
// 处理嵌套Dropdown问题
const NestingCtx = createContext<(enable: boolean) => void>(() => {});

function PcLayer(props: DropDownProps & { trigger: HTMLDivElement }) {
  const [el, setEl] = createSignal<HTMLDivElement>();
  const [, others] = splitDropProps(props);
  const [isNest, setIsNest] = createSignal(false);
  const nest = useContext(NestingCtx);
  const [visible, setVisible] = props.bind!;

  createEffect(() => {
    if (visible()) {
      nest(true);
      onCleanup(() => nest(false));
    }
  });

  /** 滚动提供者触发滚动后关闭（关闭路由弹窗时主布局会恢复 scrollTop，会误关刚打开的层） */
  const scrollElement = useScrollElement();
  if (props.closeOnScroll !== false) {
    createEventListener(
      () => (visible() ? scrollElement() : undefined),
      'scroll',
      (_e) => setVisible(false)
    );
  }

  const layerEl = createMemo(() => {
    return visible() && !isNest() ? el() : undefined;
  });

  createClickOutside(
    layerEl,
    () => {
      setTimeout(() => setVisible(false), 0);
    },
    () => {
      const t = props.trigger;
      const layer = el();
      /** 内层 Select 等下拉通过 Portal 挂在 body，不在本层 DOM 内；须一并忽略，否则会误判为「点外」关掉外层（如 VIP 抽屉） */
      if (!layer) return [t];

      const nested = Array.from(document.querySelectorAll('.dropdown-layer')).filter(
        (node) => node && node.isConnected && node.isConnected && node !== layer && !layer.contains(node)
      );

      return [t, ...nested];
    }
  );

  createEffect(() => {
    const t = props.trigger;
    const space = props.space ?? DEFAULT_SPACE;
    const placement = () => props.placement ?? 'auto';

    if (!visible()) return;

    /** 订阅 `el`，ref 在 Toggle/Transition 后挂载时 effect 会再跑，否则会只 rAF 一次且当时 el 仍为空 */
    void el();

    let positionRetryCount = 0;
    const MAX_POSITION_RETRIES = 8;

    const positionPoplayer = () => {
      if (!visible()) return;
      const poplayer = el();
      if (!poplayer || !t) return;

      if (!t.isConnected) {
        if (positionRetryCount < MAX_POSITION_RETRIES) {
          positionRetryCount += 1;
          schedulePosition();
        }
        return;
      }

      poplayer.style.setProperty('width', '');
      const targetRect = t.getBoundingClientRect();
      if (
        (targetRect.width === 0 && targetRect.height === 0) ||
        !Number.isFinite(targetRect.left) ||
        !Number.isFinite(targetRect.top)
      ) {
        if (positionRetryCount < MAX_POSITION_RETRIES) {
          positionRetryCount += 1;
          schedulePosition();
        }
        return;
      }

      const layerRect = poplayer.getBoundingClientRect();

      const topDistance = targetRect.top;
      const bottomDistance = window.innerHeight - targetRect.bottom;
      const height = t.clientHeight + layerRect.height + space;

      // y轴方向，1往下方打开，-1往上面打开,优先使用空间较多的一边
      const getVerticalDirection = () => {
        if (placement() === 'top') return -1;
        if (placement() === 'bottom' || placement() === 'bottom-center') return 1;
        // 优先往下
        if (bottomDistance >= layerRect.height + space * 2) return 1;
        return bottomDistance >= topDistance ? 1 : -1;
      };

      // 检测是否和Viewport视口Intersect
      const h = getVerticalDirection() === 1 ? bottomDistance : topDistance;
      const isIntersecting = height + space > h;

      const getLayerHeight = () => {
        return isIntersecting ? h - space * 2 : layerRect.height;
      };

      let x =
        targetRect.x + layerRect.width < window.innerWidth
          ? targetRect.x
          : Math.max(0, targetRect.x - layerRect.width + t.clientWidth);

      if (placement() === 'bottom-center') {
        x = targetRect.x + t.clientWidth / 2 - layerRect.width / 2;
        x = Math.max(0, Math.min(x, window.innerWidth - layerRect.width));
      }

      const y = getVerticalDirection() === 1 ? targetRect.bottom + space : targetRect.top - getLayerHeight() - space;

      poplayer.style.left = x + 'px';
      poplayer.style.top = y + 'px';
      const layerWidth = Math.max(layerRect.width, t.clientWidth);
      if (layerWidth > 0) {
        poplayer.style.width = layerWidth + 'px';
      }
      positionRetryCount = 0;
    };

    /** 双 rAF 等待布局；视口缩放/变化时需重新对齐触发器，否则 Portal 层像 fixed 留在旧坐标 */
    let rafTail = 0;
    const schedulePosition = () => {
      if (rafTail) return;
      rafTail = requestAnimationFrame(() => {
        rafTail = requestAnimationFrame(() => {
          rafTail = 0;
          positionPoplayer();
        });
      });
    };

    schedulePosition();

    const onViewportChange = () => schedulePosition();
    window.addEventListener('resize', onViewportChange);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onViewportChange);
    vv?.addEventListener('scroll', onViewportChange);

    const scrollTarget = scrollElement();
    const onScroll = () => schedulePosition();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => schedulePosition());
      if (t) ro.observe(t);
      const pop = el();
      if (pop) ro.observe(pop);
    }

    onCleanup(() => {
      if (rafTail) {
        cancelAnimationFrame(rafTail);
        rafTail = 0;
      }
      window.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('scroll', onViewportChange);
      scrollTarget.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    });
  });

  // const render = createMemo<boolean>((pre) => pre || visible(), visible());

  return (
    <NestingCtx.Provider value={setIsNest}>
      <Portal>
        <Toggle when={visible()}>
          <Transition.Fly y={-6}>
            <ScrollView
              {...others}
              class={cx('dropdown-layer bg-layer3', others.class)}
              ref={mergeRefs((el) => {
                // Object.assign(el.style, HIDE);
                setEl(el);
              }, props.ref)}
            >
              <Suspense>{props.layer}</Suspense>
            </ScrollView>
          </Transition.Fly>
        </Toggle>
      </Portal>
    </NestingCtx.Provider>
  );
}

interface DropProps {
  bind?: SignalLike<boolean>;
  title?: JSX.Element;
  layer?: JSX.Element;
  // trigger: HTMLDivElement;
  space?: number;
  placement?: 'auto' | 'top' | 'bottom' | 'bottom-center';
  /** 为 true 时不响应点击打开，已打开时会收起 */
  disabled?: boolean;
  /**
   * 为 false 时不监听父级 ScrollView/window 的 scroll 来关层。
   * 用于从弹窗路由退出后程序打开的下拉，避免 scroll 恢复把层立刻关掉。
   */
  closeOnScroll?: boolean;
}

interface DropDownProps extends DropProps, Omit<JSX.HTMLAttributes<HTMLDivElement>, 'title'> {
  dropdownType?: 'pop' | 'dropdown';
}
const DEFAULT_SPACE = 4;
/**
 * 这是一个dropdown的简易实现，不提供包装元素。
 * 单纯被激活的时候在当前节点后面添加一个元素，同时该元素布局是绝对定位
 *
 * 后续根据需求考虑是否将插入的元素Portal到body，但这会引起滚动问题。
 * @param props
 * @returns
 */
export function DropDown(props: DropDownProps) {
  // const [trigger, setTrigger] = createSignal<HTMLDivElement>();
  let trigger!: HTMLDivElement;
  const [layer, setLayer] = createSignal<HTMLDivElement>();
  const bind = props.bind || createSignal(false);

  createEffect(() => {
    if (props.disabled && bind[0]()) {
      bind[1](false);
    }
  });

  createEffect(() => {
    createEventListener(trigger, 'click', (_e: MouseEvent) => {
      if (props.disabled) {
        return;
      }
      const l = layer();
      if (l && _e.target && l.contains(_e.target as Element)) {
        return;
      }
      // TODO
      // https://personal-test-5y.sentry.io/issues/5897976018/?project=4507887649816576&query=is%3Aunresolved+issue.priority%3A%5Bhigh%2C+medium%5D&referrer=issue-stream&sort=freq&statsPeriod=24h&stream_index=16
      setTimeout(() => bind[1](!bind[0]()), 0);
    });
  });

  return (
    <ClosableProvider can={bind[0]()} do={() => bind[1](false)}>
      <Ref ref={trigger}>{props.children}</Ref>
      <PcLayer {...props} bind={bind} trigger={trigger} ref={setLayer} />
    </ClosableProvider>
  );
}

function splitDropProps(props: DropDownProps & { trigger: HTMLDivElement }) {
  return splitProps(props, ['title', 'bind', 'layer', 'trigger', 'disabled', 'closeOnScroll']);
}
